import { createFileRoute, isRedirect, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useSelector } from "@tanstack/react-form";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { LayoutAuth } from "#/layouts/layout.auth";
import {
  getSignupStep2DataFn,
  getSignupValidStepsFn,
  lookupBrinFn,
  submitSignupStep2Fn,
} from "#/modules/generic.signup/signup.functions";
import { signupStep2Schema } from "#/modules/generic.signup/schema.signup.step-2";
import { SchoolSearchResultTile } from "#/modules/school/components/school-search-result-tile";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useAppForm } from "#/modules/util.forms/form";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { op } from "#/modules/util.instrumentation/op";

export const Route = createFileRoute("/$locale/{-$schoolyear}/signup/step/2")({
  loader: async ({ params }) => {
    const validSteps = await getSignupValidStepsFn();
    if (!validSteps.includes(1)) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/signup/step/1",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
    return getSignupStep2DataFn();
  },
  component: SignupStep2Page,
});

function SignupStep2Page() {
  const defaults = Route.useLoaderData();
  const navigate = useNavigate();
  const submitStep2 = useServerFn(submitSignupStep2Fn);
  const lookupBrin = useServerFn(lookupBrinFn);

  const form = useAppForm({
    defaultValues: { brin: defaults.brin ?? "" },
    validators: { onSubmit: signupStep2Schema },
    onSubmit: async ({ value }) => {
      try {
        const res = await submitStep2({ data: { brin: value.brin } });
        if (res) {
          form.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
          return;
        }
        void op.track("signup_step_completed", { step: 2 });
        void navigate({
          from: Route.fullPath,
          to: "/$locale/{-$schoolyear}/signup/step/3",
          params: (prev) => prev,
        });
      } catch (error) {
        if (isRedirect(error)) throw error;
        captureUnexpectedError(error, "signup-step-2");
        form.setErrorMap({ onSubmit: { form: m["common.something_went_wrong"](), fields: {} } });
      }
    },
  });

  // Live BRIN lookup as server state — React Query owns fetching/caching/loading.
  const brin = useSelector(form.store, (s) => s.values.brin);
  const [debouncedBrin] = useDebouncedValue(brin, { wait: 400 });
  const lookup = useQuery({
    queryKey: ["signup-brin", debouncedBrin],
    queryFn: () => lookupBrin({ data: { brin: debouncedBrin } }),
    enabled: debouncedBrin.trim().length >= 4,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const schools = debouncedBrin.trim().length >= 4 ? (lookup.data?.schools ?? null) : null;
  const isSubmitting = useSelector(form.store, (s) => s.isSubmitting);

  return (
    <LayoutAuth showVisuals={false}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{m["signup.step2.title"]()}</CardTitle>
          <p className="text-muted-foreground text-sm">{m["signup.step2.subtitle"]()}</p>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.FormRoot className="flex flex-col gap-4">
              <form.AppField
                name="brin"
                children={(field) => <field.TextField label={m["brin.brin"]()} />}
              />

              {lookup.isFetching && (
                <p className="text-sm text-[var(--sea-ink-soft)]">
                  {m["school.add_school.searching_cta"]()}
                </p>
              )}

              {/* Multi-school selection */}
              {schools && schools.length > 1 && (
                <div className="flex flex-col gap-2">
                  {schools.map((school) => (
                    <SchoolSearchResultTile
                      key={school.brin}
                      school={school}
                      buttonLabel={m["common.actions.continue"]()}
                      buttonDisabled={school.isRegistered || isSubmitting}
                      onButtonClick={() => {
                        form.setFieldValue("brin", school.brin);
                        void form.handleSubmit();
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Single school preview */}
              {schools && schools.length === 1 && schools[0] && (
                <div className="rounded border p-3 text-sm">
                  <p className="font-semibold">{schools[0].schoolName}</p>
                  <p className="text-muted-foreground">
                    {schools[0].address} {schools[0].houseNumber}, {schools[0].postalCode}{" "}
                    {schools[0].city}
                  </p>
                </div>
              )}

              {/* Button hidden when a multi-school list is shown (user picks from the list). */}
              {!(schools && schools.length > 1) && (
                <form.SubmitButton className="mt-2 w-full">
                  {m["common.actions.next"]()}
                </form.SubmitButton>
              )}
            </form.FormRoot>
          </form.AppForm>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
